import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import BaseDialog from "./Framework/BaseDialog";
import supabase from "../../../../utils/supabase";

const trim = (f) => ({
  id: String(f.id || "").trim(),
  issn: String(f.issn || "").trim(),
  publisher: String(f.publisher || "").trim(),
  name_english: String(f.name_english || "").trim(),
  name_original: String(f.name_original || "").trim(),
});

/**
 * Journal 追加/編集 共通ダイアログ（BaseDialog 使用）
 *
 * props:
 * - open: boolean
 * - mode?: "add" | "edit"  // journal があれば自動的に "edit"
 * - journal?: { id, issn, publisher, name_english, name_original }
 * - onClose?: (saved?: boolean) => void  // 保存時 true
 * - onSaved?: (row) => void              // 追加/更新後のレコードを返す
 */
export default function DialogJournalUpsert({
  open,
  mode,
  journal,
  onClose,
  onSaved,
}) {
  const isEdit = useMemo(() => (mode ? mode === "edit" : !!journal), [mode, journal]);

  const [form, setForm] = useState({ id: "", issn: "", publisher: "", name_english: "", name_original: "" });
  const [errors, setErrors] = useState({ id: false, name_english: false, name_original: false });
  const [loading, setLoading] = useState(false);

  const initialRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const base = isEdit && journal ? trim(journal) : { id: "", issn: "", publisher: "", name_english: "", name_original: "" };
    setForm(base);
    setErrors({ id: false, name_english: false, name_original: false });
    initialRef.current = JSON.stringify(base);
  }, [open, isEdit, journal]);

  const isDirty = useMemo(() => {
    if (!initialRef.current) return false;
    return JSON.stringify(trim(form)) !== initialRef.current;
  }, [form]);

  const setField = useCallback((k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: false }));
  }, []);

  const validate = useCallback((f) => {
    const next = {
      id: isEdit ? false : !f.id,
      name_english: !f.name_english,
      name_original: !f.name_original,
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  }, [isEdit]);

  const handleSubmit = useCallback(async () => {
    const payload = trim(form);
    if (!validate(payload)) return;
    setLoading(true);
    try {
      if (!isEdit) {
        const { data: dup, error: dupErr } = await supabase.from("journals").select("id").eq("id", payload.id);
        if (dupErr) throw dupErr;
        if (dup?.length) {
          alert("Journal ID already exists. Please use a unique ID.");
          return;
        }
        const { data: inserted, error: insErr } = await supabase.from("journals").insert([payload]).select().single();
        if (insErr) throw insErr;
        onSaved?.(inserted);
      } else {
        const { id, ...rest } = payload; // id は更新不可
        const { data: updated, error: upErr } = await supabase
          .from("journals")
          .update(rest)
          .eq("id", journal.id)
          .select()
          .single();
        if (upErr) throw upErr;
        onSaved?.(updated);
      }
      onClose?.(true);
    } catch (e) {
      console.error(e);
      alert(`Failed to ${isEdit ? "update" : "add"} journal: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [form, isEdit, journal, onClose, onSaved, validate]);

  const headerAddon = (
    <Box sx={{ px: 2.5, py: 1.25, display: "flex", gap: 2, alignItems: "center" }}>
      <Typography variant="body2" sx={{ opacity: 0.75 }}>
        {isEdit ? "Editing existing journal" : "Create a new journal"}
      </Typography>
      <Box sx={{ ml: "auto", fontSize: 12, opacity: 0.65 }}>
        Display name:&nbsp;
        <strong>{form.name_english || form.name_original || form.code || "(no name)"}</strong>
      </Box>
    </Box>
  );

  return (
    <BaseDialog
      open={open}
      title={isEdit ? "Edit Journal" : "Add New Journal"}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText={isEdit ? "Update" : "Add"}
      loading={loading}
      confirmCloseWhen={!loading && isDirty}
      confirmCloseMessage="You have unsaved changes. Close without saving?"
      headerAddon={headerAddon}
      maxWidth="sm"
      fullWidth
    >
      <Stack spacing={1.5}>
        <TextField
          label="ID"
          value={form.id}
          onChange={(e) => setField("id", e.target.value)}
          fullWidth
          required
          disabled={isEdit}
          error={errors.id}
          helperText={
            isEdit
              ? "ID cannot be changed."
              : errors.id
              ? "ID is required."
              : "Set a unique ID (e.g., Zootaxa). This cannot be changed later."
          }
        />
        <TextField label="ISSN" value={form.issn} onChange={(e) => setField("issn", e.target.value)} fullWidth />
        <TextField label="Publisher" value={form.publisher} onChange={(e) => setField("publisher", e.target.value)} fullWidth />
        <TextField
          label="Journal Name (English)"
          value={form.name_english}
          onChange={(e) => setField("name_english", e.target.value)}
          fullWidth
          required
          error={errors.name_english}
          helperText={errors.name_english ? "English name is required." : "*Required"}
        />
        <TextField
          label="Journal Name (Original Language)"
          value={form.name_original}
          onChange={(e) => setField("name_original", e.target.value)}
          fullWidth
          required
          error={errors.name_original}
          helperText={errors.name_original ? "Original name is required." : "*Required"}
        />
      </Stack>
    </BaseDialog>
  );
}
